import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  Tabs,
  Tab,
  Chip,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, RestoreFromTrash as RestoreIcon, FilterListOff as FilterListOffIcon } from '@mui/icons-material';
import { CotisationSociale, CotisationSocialeCreate, CotisationSocialeUpdate } from '../../../types/paie';
import { cotisationsSocialesAPI } from '../../../services/api';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import useFormDraft from '../../../hooks/useFormDraft';
import PermissionButton from '../../../components/PermissionButton';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { SlideUpTransition, useMinimizable, DialogControls, MinimizedBar } from '../../../components/MinimizableDialog';

const HR_COLOR = '#00897B';

const COTISATION_FILTER_SCHEMA = {
  nom:       { type: 'text', param: 'nom' },
  taux:      { type: 'numberRange', paramMin: 'taux_min', paramMax: 'taux_max' },
  condition: { type: 'text', param: 'condition' },
  is_active: { type: 'select', param: 'is_active' },
} as const;

const STATUT_OPTIONS = [
  { value: 'true', label: 'Actif' },
  { value: 'false', label: 'Inactif' },
];

const CotisationSocialeManagement: React.FC = () => {
  const [cotisations, setCotisations] = useState<CotisationSociale[]>([]);
  const [deletedCotisations, setDeletedCotisations] = useState<CotisationSociale[]>([]);
  const [open, setOpen] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(open);
  const [editMode, setEditMode] = useState(false);
  const [editingCotisation, setEditingCotisation] = useState<CotisationSociale | null>(null);
  const [currentCotisation, setCurrentCotisation] = useState<Partial<CotisationSociale>>({
    nom: '',
    taux: 0,
    condition: '',
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { filters, update, reset, hasActive } = useColumnFilters(COTISATION_FILTER_SCHEMA);

  const filteredCotisations = useMemo(
    () =>
      cotisations.filter((c) => {
        if (filters.nom && !c.nom.toLowerCase().includes(filters.nom.toLowerCase())) return false;
        if (filters.condition && !(c.condition || '').toLowerCase().includes(filters.condition.toLowerCase())) return false;
        if (filters.taux.min !== '' && Number(c.taux) < Number(filters.taux.min)) return false;
        if (filters.taux.max !== '' && Number(c.taux) > Number(filters.taux.max)) return false;
        if (filters.is_active !== '' && Boolean(c.is_active) !== (filters.is_active === 'true')) return false;
        return true;
      }),
    [cotisations, filters]
  );

  useEffect(() => {
    fetchCotisations();
  }, []);

  const fetchCotisations = async () => {
    try {
      setLoading(true);
      const data = await cotisationsSocialesAPI.getAll();
      setCotisations(data.items);
    } catch (error) {
      showError('Erreur lors du chargement des cotisations sociales');
      console.error('Error fetching cotisations sociales:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedCotisations = async () => {
    try {
      setLoading(true);
      const data = await cotisationsSocialesAPI.getDeleted();
      setDeletedCotisations(data.items);
    } catch (error) {
      showError('Erreur lors du chargement des cotisations supprimées');
      console.error('Error fetching deleted cotisations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (cotisation: CotisationSociale) => {
    try {
      setLoading(true);
      await cotisationsSocialesAPI.restore(cotisation.id);
      showSuccess('Cotisation sociale restaurée avec succès');
      fetchDeletedCotisations();
      fetchCotisations();
    } catch (error) {
      showError('Erreur lors de la restauration de la cotisation');
      console.error('Error restoring cotisation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 1) {
      fetchDeletedCotisations();
    }
  };

  // Cache an in-progress new cotisation so an accidental backdrop-close doesn't wipe it.
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    currentCotisation: Partial<CotisationSociale>;
  }>('cotisation-sociale');

  const handleOpen = (cotisation?: CotisationSociale) => {
    restore();
    if (cotisation) {
      setEditingCotisation(cotisation);
      setCurrentCotisation({
        nom: cotisation.nom,
        taux: cotisation.taux,
        condition: cotisation.condition || '',
        is_active: cotisation.is_active,
      });
      setEditMode(true);
    } else {
      setEditingCotisation(null);
      const draft = loadDraft();
      setCurrentCotisation(
        draft?.currentCotisation ?? {
          nom: '',
          taux: 0,
          condition: '',
          is_active: true,
        }
      );
      setEditMode(false);
    }
    setOpen(true);
  };

  // X / Annuler = deliberate cancel: wipe inputs and discard any draft.
  const handleClose = () => {
    clearDraft();
    setMinimized(false);
    setOpen(false);
    setEditingCotisation(null);
    setCurrentCotisation({
      nom: '',
      taux: 0,
      condition: '',
      is_active: true,
    });
    setEditMode(false);
  };

  // Backdrop click / Escape = accidental: keep new-cotisation work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') return;
    if (!editMode) {
      saveDraft({ currentCotisation });
      setOpen(false);
    } else {
      handleClose();
    }
  };

  const handleSave = async () => {
    if (!currentCotisation.nom?.trim()) {
      showError('Le nom est obligatoire');
      return;
    }

    if (currentCotisation.taux === undefined || currentCotisation.taux < 0 || currentCotisation.taux > 100) {
      showError('Le taux doit être entre 0 et 100');
      return;
    }

    try {
      setLoading(true);
      if (editMode && editingCotisation) {
        const updateData: CotisationSocialeUpdate = {
          nom: currentCotisation.nom,
          taux: currentCotisation.taux,
          condition: currentCotisation.condition || undefined,
          is_active: currentCotisation.is_active,
        };
        await cotisationsSocialesAPI.update(editingCotisation.id, updateData);
        showSuccess('Cotisation sociale modifiée avec succès');
      } else {
        const createData: CotisationSocialeCreate = {
          nom: currentCotisation.nom!,
          taux: currentCotisation.taux!,
          condition: currentCotisation.condition || undefined,
          is_active: currentCotisation.is_active,
        };
        await cotisationsSocialesAPI.create(createData);
        showSuccess('Cotisation sociale créée avec succès');
      }
      await fetchCotisations();
      handleClose();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (cotisation: CotisationSociale) => {
    showConfirm(
      `Êtes-vous sûr de vouloir supprimer la cotisation "${cotisation.nom}" ?`,
      async () => {
        try {
          setLoading(true);
          await cotisationsSocialesAPI.delete(cotisation.id);
          showSuccess('Cotisation sociale supprimée avec succès');
          await fetchCotisations();
        } catch (error) {
          showError('Erreur lors de la suppression de la cotisation');
          console.error('Error deleting cotisation:', error);
        } finally {
          setLoading(false);
        }
      }
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Gestion des Cotisations Sociales</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tabValue === 0 && hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary' }}
            >
              Effacer les filtres
            </Button>
          )}
          {tabValue === 0 && (
            <PermissionButton
              moduleName="HR"
              operation="add"
              miniModuleName="Cotisations Sociales"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpen()}
            >
              Nouvelle Cotisation
            </PermissionButton>
          )}
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Cotisations Actives" />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Box sx={getModuleTableTheme('hr')}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Nom
                      <ColumnFilter type="text" value={filters.nom} onChange={(v) => update('nom', v)} label="nom" color={HR_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Taux (%)
                      <ColumnFilter type="numberRange" value={filters.taux} onChange={(v) => update('taux', v)} label="taux" color={HR_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Condition / Description
                      <ColumnFilter type="text" value={filters.condition} onChange={(v) => update('condition', v)} label="condition" color={HR_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Statut
                      <ColumnFilter type="select" value={filters.is_active} onChange={(v) => update('is_active', v)} options={STATUT_OPTIONS} label="statut" color={HR_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCotisations.map((cotisation) => (
                  <TableRow key={cotisation.id}>
                    <TableCell>{cotisation.nom}</TableCell>
                    <TableCell>
                      <Typography fontWeight="medium">
                        {Number(cotisation.taux).toFixed(2)} %
                      </Typography>
                    </TableCell>
                    <TableCell>{cotisation.condition || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={cotisation.is_active ? 'Actif' : 'Inactif'}
                        color={cotisation.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <PermissionButton
                        moduleName="HR"
                        operation="edit"
                        miniModuleName="Cotisations Sociales"
                        component="IconButton"
                        onClick={() => handleOpen(cotisation)}
                        color="primary"
                      >
                        <EditIcon />
                      </PermissionButton>
                      <PermissionButton
                        moduleName="HR"
                        operation="delete"
                        miniModuleName="Cotisations Sociales"
                        component="IconButton"
                        onClick={() => handleDelete(cotisation)}
                        color="error"
                      >
                        <DeleteIcon />
                      </PermissionButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredCotisations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {hasActive() ? 'Aucune cotisation ne correspond aux filtres' : 'Aucune cotisation sociale trouvée'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tabValue === 1 && (
        <Box sx={getModuleTableTheme('hr')}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Taux (%)</TableCell>
                  <TableCell>Condition / Description</TableCell>
                  <TableCell>Date de Suppression</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deletedCotisations.map((cotisation) => (
                  <TableRow key={cotisation.id}>
                    <TableCell>{cotisation.nom}</TableCell>
                    <TableCell>{Number(cotisation.taux).toFixed(2)} %</TableCell>
                    <TableCell>{cotisation.condition || '-'}</TableCell>
                    <TableCell>
                      {cotisation.deleted_at ? new Date(cotisation.deleted_at).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => {
                          showConfirm(
                            `Êtes-vous sûr de vouloir restaurer la cotisation "${cotisation.nom}" ?`,
                            () => handleRestore(cotisation),
                            {
                              title: 'Confirmer la restauration',
                              confirmText: 'Restaurer',
                              cancelText: 'Annuler',
                            }
                          );
                        }}
                        color="primary"
                        title="Restaurer"
                      >
                        <RestoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {deletedCotisations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Aucune cotisation supprimée
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth TransitionComponent={SlideUpTransition}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editMode ? 'Modifier la Cotisation Sociale' : 'Nouvelle Cotisation Sociale'}
          <DialogControls color="#009688" onMinimize={minimize} onClose={handleClose} />
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                variant="filled"
                fullWidth
                label="Nom"
                value={currentCotisation.nom || ''}
                onChange={(e) => setCurrentCotisation({ ...currentCotisation, nom: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                variant="filled"
                fullWidth
                label="Taux"
                type="number"
                value={currentCotisation.taux ?? ''}
                onChange={(e) => setCurrentCotisation({ ...currentCotisation, taux: parseFloat(e.target.value) || 0 })}
                required
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 0.01,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="filled"
                fullWidth
                label="Condition / Description"
                value={currentCotisation.condition || ''}
                onChange={(e) => setCurrentCotisation({ ...currentCotisation, condition: e.target.value })}
                multiline
                rows={3}
                placeholder="Décrivez les conditions d'application de cette cotisation..."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentCotisation.is_active ?? true}
                    onChange={(e) => setCurrentCotisation({ ...currentCotisation, is_active: e.target.checked })}
                  />
                }
                label="Actif"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {editMode ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={open && minimized}
        color="#009688"
        title={editMode ? 'Modifier la Cotisation Sociale' : 'Nouvelle Cotisation Sociale'}
        onRestore={restore}
        onClose={handleClose}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default CotisationSocialeManagement;
